<template>
  <div
    v-if="personish"
    class="base-attendee"
    :class="{ isPerson, isOpenable }"
    @click="openPerson(personish.email, $event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="left">
      <Avatar :personish="personish" :going="attendee.status" />
      <div
        v-if="isPerson"
        class="person"
        :class="{ organizer: attendee.organizer, tint }"
      >
        <span class="name">{{
          isOwner
            ? "You"
            : (personish.kind === "person" && personish.name?.display) ||
              personish.email
        }}</span>
        <BaseEmail :email="personish.email" size="small" />
      </div>
      <div
        v-else
        class="attendee"
        :class="{
          organizer: attendee.organizer,
        }"
      >
        <BaseEmail :email="personish.email" />
      </div>
    </div>
    <!-- We allow delete organizer if he's alone to prevent the user from being locked at 1 user, even if this
  should never happen.-->
    <CloseButton
      v-if="isDeletable"
      class="close"
      text="Remove"
      @click="deleteAttendee(attendee)"
    />
  </div>
</template>
<script lang="ts">
import { isObject } from "lodash-es";
import { computed, defineComponent, PropType, toRefs } from "vue";

import { Attendee, People } from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseEmail from "@/components/base/BaseEmail.vue";
import Avatar, { Personish } from "@/components/elements/Avatar.vue";
import CloseButton from "@/components/elements/CloseButton.vue";
import * as inject from "@/components/inject";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { navigateToObject } from "@/utils/navigate";

export default defineComponent({
  components: {
    Avatar,
    BaseEmail,
    CloseButton,
  },
  props: {
    attendee: { type: Object as PropType<Attendee>, required: true },
    isDeletable: { type: Boolean, default: false },
    isOpenable: { type: Boolean, default: true },
    isOwner: { type: Boolean, default: false },
    tint: { type: String as PropType<"default" | "blue">, default: "default" },
  },
  emits: {
    deleteAttendee: (_: Attendee) => true,
  },
  async setup(props, { emit }) {
    const { attendee, isOpenable } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();
    const contextMenu = inject.contextMenu();
    const releaser = useReleaser();
    const personish = await releaser.add(
      bindAsync(attendee, async (attendee) => {
        const person = await controller.people.byEmail(attendee.email);
        person.release();
        if (person.value) {
          return person.map((person: People | null) => {
            if (person) {
              const p: Personish = {
                kind: "person",
                ...person,
                email: attendee.email,
              };
              return p;
            } else {
              const a: Personish = { kind: "attendee", ...attendee };
              return a;
            }
          });
        } else {
          const a: Personish = { kind: "attendee", ...attendee };
          return releasableRef(a);
        }
      })
    );
    const isPerson = computed<boolean>(
      () =>
        personish.value?.kind === "person" &&
        !!(
          isObject(personish.value.name) &&
          "display" in personish.value.name &&
          personish.value.name.display
        )
    );
    const copyEmail = async (email: string) => {
      await copy(email);
      controller.notifications.notify(`${email} copied to clipboard`);
    };
    const openPerson = async (email: string, mouseEvent?: MouseEvent) => {
      if (!isOpenable.value) return;
      const person = await controller.people.byEmail(email);
      person.release();
      if (person.value)
        navigateToObject(
          router,
          controller,
          {
            kind: "people",
            id: person.value.id,
          },
          mouseEvent
        );
    };
    const deleteAttendee = (attendee: Attendee) =>
      emit("deleteAttendee", attendee);
    const showContextMenu = async (e: MouseEvent) => {
      const person = await controller.people.byEmail(attendee.value.email);
      person.release();
      if (person.value) {
        contextMenu.value.show({ type: "people", value: person.value }, e);
      }
    };
    return {
      copyEmail,
      deleteAttendee,
      isPerson,
      openPerson,
      personish,
      showContextMenu,
    };
  },
});
</script>
<style lang="scss" scoped>
.base-attendee {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 10px;
  padding: 2px 8px;
  min-height: 32px;
  border-radius: $border-radius-m;
  border: 1px solid transparent;
  @include transition(background-color 100ms ease);

  &.isPerson.isOpenable:hover {
    @include themed() {
      background-color: t($grey-10);
    }
  }

  &.isPerson.isOpenable:active {
    @include themed() {
      background-color: t($grey-15);
    }
  }

  .left {
    display: flex;
    flex-direction: row;
    column-gap: 10px;
    min-width: 0;
    align-items: center;
    @extend %font-caption-regular;
    .avatar {
      flex-shrink: 0;
    }
    .organizer {
      @extend %font-caption-medium;
    }
    .attendee {
      display: flex;
      text-align: start;
      margin: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      @include themed() {
        color: t($primary);
      }
      &.blue {
        @include themed() {
          color: t($tertiary-50);
        }
      }
    }
    .person {
      display: flex;
      flex-direction: column;
    }
  }
  .close {
    display: none;
    padding: 0;
    flex-shrink: 0;
  }

  &:hover .close {
    display: block;
  }
}
</style>
